/**
 * File Upload View Component - Document Triage System
 *
 * Mobile-friendly single-column layout with document quality control workflow:
 * - Upload → Process → Review → Approve/Reject → Knowledge Base
 * - Comprehensive triage modal with preview, metadata, and statistics
 */

import React, { useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useFileUploadStore } from '../stores/fileUploadStore';
import { useAuthStore } from '../stores/authStore';
import { updateApprovalStatus, updateFileMetadata } from '../api/file-upload';
import type { UploadedFile } from '../api/file-upload';
import { useFileProcessingWebSocket, type FileProcessingUpdate } from '../hooks/useFileProcessingWebSocket';
import FolderSelector from './FolderSelector';

const FileUploadView: React.FC = () => {
  const {
    files,
    selectedFile,
    total,
    isLoading,
    isUploading,
    uploadProgress,
    filters,
    hasMore,
    loadFiles,
    uploadNewFile,
    selectFile,
    refreshFile,
    processSelectedFile,
    downloadFile,
    deleteSelectedFile,
    setFilters,
    clearFilters,
  } = useFileUploadStore();

  const { user } = useAuthStore();

  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [chunkingStrategy, setChunkingStrategy] = useState<'paragraphs' | 'sections' | 'pages' | 'articles'>('paragraphs');
  const [showTriageModal, setShowTriageModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'preview' | 'metadata' | 'statistics'>('preview');
  const [showRejected, setShowRejected] = useState(false);
  const [isUpdatingApproval, setIsUpdatingApproval] = useState(false);
  const [previewData, setPreviewData] = useState<{
    extracted_text: string;
    full_text_length: number;
    metadata: any;
  } | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Metadata editing state
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [metadataEditForm, setMetadataEditForm] = useState({
    denomination_id: '',
    document_type: '',
    is_official: false,
    trigger_words: '',
    extracted_title: '',
    folder_path: '/',
  });

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Upload confirmation workflow state (Step 1: Select → Step 2: Configure → Step 3: Confirm)
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showUploadConfirmModal, setShowUploadConfirmModal] = useState(false);
  const [uploadConfigFormData, setUploadConfigFormData] = useState({
    denomination_id: 'cogic',
    document_type: 'confession_of_faith',
    is_official: true,
    folder_path: '',
  });

  // Real-time processing state
  const [processingUpdates, setProcessingUpdates] = useState<Map<string, FileProcessingUpdate>>(new Map());

  // WebSocket connection for real-time file processing updates
  useFileProcessingWebSocket({
    onUpdate: (update: FileProcessingUpdate) => {
      // Store the update in state
      setProcessingUpdates(prev => {
        const newMap = new Map(prev);
        newMap.set(update.file_id, update);
        return newMap;
      });

      // If the file is completed or failed, refresh the file list
      if (update.status === 'completed' || update.status === 'failed') {
        // Refresh after a short delay to ensure backend has updated
        setTimeout(() => {
          loadFiles(false);
        }, 1000);
      }

      // If this is the currently selected file, refresh it
      if (selectedFile && update.file_id === selectedFile.id) {
        setTimeout(() => {
          refreshFile(update.file_id);
        }, 1000);
      }
    },
    onError: (error) => {
      // WebSocket errors are handled silently - connection will auto-retry
    },
    autoReconnect: true
  });

  useEffect(() => {
    loadFiles(true);
  }, []);

  useEffect(() => {
    if (selectedFile) {
      setShowTriageModal(true);
      setActiveTab('preview');
    }
  }, [selectedFile]);

  useEffect(() => {
    // Load preview data when modal opens and preview tab is active
    if (showTriageModal && selectedFile && activeTab === 'preview') {
      fetchDocumentPreview(selectedFile.id);
    }
  }, [showTriageModal, selectedFile, activeTab]);

  // Auto-refresh file status when processing but no WebSocket updates
  useEffect(() => {
    if (!selectedFile || selectedFile.processing_status !== 'processing') {
      return;
    }

    const processingUpdate = processingUpdates.get(selectedFile.id);

    // Only poll if no WebSocket updates have arrived
    if (!processingUpdate) {
      const interval = setInterval(() => {
        refreshFile(selectedFile.id);
      }, 5000); // Poll every 5 seconds

      return () => {
        clearInterval(interval);
      };
    }
  }, [selectedFile, processingUpdates, refreshFile]);

  const fetchDocumentPreview = async (fileId: string) => {
    setIsLoadingPreview(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      const response = await fetch(`/api/file-uploads/${fileId}/extract`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to load preview' }));
        throw new Error(errorData.detail || 'Failed to load preview');
      }

      const data = await response.json();
      setPreviewData({
        extracted_text: data.extracted_text,
        full_text_length: data.full_text_length,
        metadata: data.metadata
      });
    } catch (error: any) {
      console.error('Failed to fetch document preview:', error);
      setPreviewError(error.message || 'Failed to load document preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelected(droppedFiles[0]);
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFileSelected(selectedFiles[0]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Step 1: Handle file selection (validate and show config modal)
  const handleFileSelected = (file: File) => {
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/markdown'];
    const validExtensions = ['.pdf', '.docx', '.md'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      alert('Invalid file type. Please upload PDF, DOCX, or Markdown files.');
      return;
    }

    // Store the file and initialize config with current defaults
    setPendingFile(file);
    setUploadConfigFormData({
      denomination_id: uploadConfigFormData.denomination_id,
      document_type: uploadConfigFormData.document_type,
      is_official: uploadConfigFormData.is_official,
      folder_path: uploadConfigFormData.folder_path,
    });
    setShowUploadConfirmModal(true);
  };

  // Step 3: Confirm and upload the file
  const handleConfirmUpload = async () => {
    if (!pendingFile) return;

    // Validate folder selection is required
    if (!uploadConfigFormData.folder_path || uploadConfigFormData.folder_path.trim() === '') {
      alert('Please select a folder before uploading. Folder selection is required to properly organize denominational knowledge.');
      return;
    }

    // Close modal and upload
    setShowUploadConfirmModal(false);

    const uploadedFile = await uploadNewFile({
      file: pendingFile,
      denomination_id: uploadConfigFormData.denomination_id,
      document_type: uploadConfigFormData.document_type,
      is_official: uploadConfigFormData.is_official,
      folder_path: uploadConfigFormData.folder_path,
    });

    if (uploadedFile) {
      selectFile(uploadedFile);
    }

    // Clear pending state
    setPendingFile(null);
  };

  // Cancel upload
  const handleCancelUpload = () => {
    setShowUploadConfirmModal(false);
    setPendingFile(null);
  };

  const handleProcessFile = async () => {
    if (!selectedFile) return;
    await processSelectedFile({ chunking_strategy: chunkingStrategy });

    const pollInterval = setInterval(async () => {
      if (selectedFile) {
        await refreshFile(selectedFile.id);
        const updatedFile = files.find(f => f.id === selectedFile.id);
        if (updatedFile && (updatedFile.processing_status === 'completed' || updatedFile.processing_status === 'failed')) {
          clearInterval(pollInterval);
        }
      }
    }, 3000);

    setTimeout(() => clearInterval(pollInterval), 300000);
  };

  const handleApprovalStatusUpdate = async (status: 'approved' | 'rejected') => {
    if (!selectedFile) return;

    setIsUpdatingApproval(true);
    try {
      await updateApprovalStatus(selectedFile.id, status);
      await refreshFile(selectedFile.id);
      setShowTriageModal(false);
      selectFile(null);
    } catch (error) {
      console.error('Failed to update approval status:', error);
      alert('Failed to update approval status. Please try again.');
    } finally {
      setIsUpdatingApproval(false);
    }
  };

  const handleEditMetadata = () => {
    if (!selectedFile) return;

    // Initialize form with current values
    setMetadataEditForm({
      denomination_id: selectedFile.denomination_id,
      document_type: selectedFile.document_type,
      is_official: selectedFile.is_official,
      trigger_words: selectedFile.trigger_words || '',
      extracted_title: selectedFile.extracted_title || '',
      folder_path: selectedFile.folder_path || '/',
    });
    setIsEditingMetadata(true);
  };

  const handleCancelEdit = () => {
    setIsEditingMetadata(false);
    setMetadataEditForm({
      denomination_id: '',
      document_type: '',
      is_official: false,
      trigger_words: '',
      extracted_title: '',
      folder_path: '/',
    });
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFile) return;

    setIsDeleting(true);
    try {
      await deleteSelectedFile();
      setShowDeleteConfirm(false);
      setShowTriageModal(false);
      // Refresh file list
      await loadFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
      alert('Failed to delete file. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleSaveMetadata = async () => {
    if (!selectedFile) return;

    setIsSavingMetadata(true);
    try {
      await updateFileMetadata(selectedFile.id, metadataEditForm);
      await refreshFile(selectedFile.id);
      setIsEditingMetadata(false);
      alert('Metadata updated successfully!');
    } catch (error) {
      console.error('Failed to update metadata:', error);
      alert('Failed to update metadata. Please try again.');
    } finally {
      setIsSavingMetadata(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getProcessingStatusBadge = (status: string): string => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'processing': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'failed': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-dark-bg/50 text-dark-text-muted border-dark-border';
    }
  };

  const getApprovalStatusBadge = (status: string): { class: string; label: string } => {
    switch (status) {
      case 'approved':
        return { class: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Approved' };
      case 'rejected':
        return { class: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Rejected' };
      case 'pending_review':
        return { class: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Review Needed' };
      default:
        return { class: 'bg-dark-bg/50 text-dark-text-muted border-dark-border', label: status };
    }
  };

  // Helper function to get processing status explanation
  const getProcessingStatusTooltip = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Document is queued and will be processed automatically';
      case 'processing':
        return 'Currently extracting text, creating sections, and generating AI embeddings';
      case 'completed':
        return 'Processing finished successfully - document is ready for review';
      case 'failed':
        return 'Processing encountered an error - see details for recovery options';
      default:
        return status;
    }
  };

  // Helper function to get approval status explanation
  const getApprovalStatusTooltip = (status: string): string => {
    switch (status) {
      case 'pending_review':
        return 'Document needs your review before being added to the Knowledge Base';
      case 'approved':
        return 'Document approved and searchable in your Knowledge Base';
      case 'rejected':
        return 'Document rejected - will not appear in search results';
      default:
        return status;
    }
  };

  const handleClearFilters = () => {
    clearFilters();
    setSearchQuery('');
    setShowRejected(false);
  };

  const hasActiveFilters = filters.denomination_id || filters.processing_status || searchQuery || showRejected;

  // Deduplicate files by ID and apply filters
  const uniqueFiles = Array.from(
    new Map(files.map(file => [file.id, file])).values()
  );

  const filteredFiles = uniqueFiles.filter(file => {
    // Apply rejected filter
    if (!showRejected && file.approval_status === 'rejected') return false;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return file.original_filename.toLowerCase().includes(query) ||
             file.extracted_title?.toLowerCase().includes(query);
    }

    return true;
  });

  return (
    <div
      className="flex-1 flex flex-col h-full bg-dark-bg text-dark-text"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag and Drop Overlay */}
      {isDragging && (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className="fixed inset-0 z-50 bg-blue-500/20 backdrop-blur-sm flex items-center justify-center"
        >
          <div className="bg-dark-card border-2 border-dashed border-accent-blue rounded-lg p-6 sm:p-12 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-16 h-16 mx-auto mb-4 text-accent-blue">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <div className="text-xl font-bold mb-2">Drop file here to upload</div>
            <div className="text-sm text-dark-text-muted">PDF, DOCX, or Markdown files</div>
          </div>
        </div>
      )}

      {/* Full-width Header - Search/Filter Panel */}
      <div className="p-3 sm:p-4 border-b border-dark-border">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Document Triage</h1>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="bg-gradient-to-r from-accent-blue to-accent-orange text-white px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2 text-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Upload File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.md"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>

        {/* Search and Filters Row */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search files..."
              className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-accent-blue"
            />
          </div>
          <select
            value={filters.denomination_id || ''}
            onChange={(e) => setFilters({ denomination_id: e.target.value || undefined })}
            className="bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-accent-blue"
          >
            <option value="">All Denominations</option>
            <option value="presbyterian">Presbyterian</option>
            <option value="southern_baptist">Southern Baptist</option>
            <option value="methodist">Methodist</option>
            <option value="lutheran">Lutheran</option>
            <option value="anglican">Anglican</option>
          </select>
          <select
            value={filters.processing_status || ''}
            onChange={(e) => setFilters({ processing_status: e.target.value || undefined })}
            className="bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-accent-blue"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <label className="flex items-center gap-2 bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm cursor-pointer hover:border-accent-blue transition-colors">
            <input
              type="checkbox"
              checked={showRejected}
              onChange={(e) => setShowRejected(e.target.checked)}
              className="w-4 h-4 accent-accent-blue"
            />
            <span className="text-dark-text">Show Rejected</span>
          </label>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="bg-dark-card hover:bg-bridge-hover border border-dark-border text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        <div className="flex items-center justify-between text-sm text-dark-text-muted">
          <span>{filteredFiles.length} of {total} files</span>
          {isUploading && <span className="text-blue-400">Uploading... {uploadProgress}%</span>}
        </div>

        {/* Upload Progress Bar */}
        {isUploading && (
          <div className="mt-3 w-full bg-dark-border rounded-full h-2">
            <div
              className="bg-accent-blue h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>

      {/* Full-width File List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && files.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-accent-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <div className="text-dark-text-muted">Loading files...</div>
            </div>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-20 h-20 text-dark-text-muted mb-4 opacity-40 mx-auto">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              <p className="text-lg font-semibold text-dark-text mb-2">
                {searchQuery || hasActiveFilters ? 'No files found' : 'No files uploaded yet'}
              </p>
              <p className="text-dark-text-muted max-w-md">
                {searchQuery || hasActiveFilters ? 'Try adjusting your search or filters' : 'Upload a file to get started'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredFiles.map((file) => {
                const approvalBadge = getApprovalStatusBadge(file.approval_status);
                const needsReview = file.processing_status === 'completed' && file.approval_status === 'pending_review';

                return (
                  <div
                    key={file.id}
                    onClick={() => selectFile(file)}
                    className={`bg-dark-card hover:bg-bridge-hover border rounded-lg p-3 sm:p-4 cursor-pointer transition-all ${
                      needsReview
                        ? 'border-blue-500/50 ring-2 ring-blue-500/20'
                        : file.approval_status === 'rejected'
                        ? 'border-red-500/30 opacity-60'
                        : 'border-dark-border hover:border-accent-blue/50'
                    }`}
                  >
                    {/* File Icon and Type */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-accent-blue">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        <span className="text-xs font-bold text-dark-text-muted uppercase">{file.file_type}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span
                          className={`text-xs px-2 py-1 rounded border ${getProcessingStatusBadge(file.processing_status)} cursor-help transition-opacity hover:opacity-80`}
                          title={getProcessingStatusTooltip(file.processing_status)}
                        >
                          {file.processing_status}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded border ${approvalBadge.class} cursor-help transition-opacity hover:opacity-80`}
                          title={getApprovalStatusTooltip(file.approval_status)}
                        >
                          {approvalBadge.label}
                        </span>
                      </div>
                    </div>

                    {/* File Name and Title */}
                    <div className="mb-3">
                      <div className="font-medium text-white truncate mb-1" title={file.original_filename}>
                        {file.original_filename}
                      </div>
                      {file.extracted_title && (
                        <div className="text-sm text-dark-text-muted line-clamp-2" title={file.extracted_title}>
                          {file.extracted_title}
                        </div>
                      )}
                    </div>

                    {/* File Metadata */}
                    <div className="flex items-center justify-between text-xs text-dark-text-muted">
                      <span>{formatFileSize(file.file_size_bytes)}</span>
                      {file.chunks_created !== null && file.chunks_created > 0 && (
                        <span className="text-green-400">{file.chunks_created} chunks</span>
                      )}
                    </div>

                    {/* Review Needed Badge */}
                    {needsReview && (
                      <div className="mt-3 pt-3 border-t border-dark-border">
                        <div className="text-xs font-semibold text-blue-400 flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                          </svg>
                          Click to Review
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {hasMore && !searchQuery && (
              <div className="p-3 sm:p-4 text-center border-t border-dark-border">
                <button
                  onClick={() => loadFiles(false)}
                  disabled={isLoading}
                  className="bg-dark-card hover:bg-bridge-hover border border-dark-border text-white px-6 py-2 rounded-lg text-sm transition-colors"
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Document Triage Modal */}
      {showTriageModal && selectedFile && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
          <div className="bg-dark-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-3 sm:p-4 lg:p-6 border-b border-dark-border">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-white mb-2 truncate">{selectedFile.original_filename}</h2>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-bold text-dark-text-muted uppercase">{selectedFile.file_type}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded border ${getProcessingStatusBadge(selectedFile.processing_status)} cursor-help transition-opacity hover:opacity-80`}
                      title={getProcessingStatusTooltip(selectedFile.processing_status)}
                    >
                      {selectedFile.processing_status}
                    </span>
                    <span
                      className={`text-xs px-2 py-1 rounded border ${getApprovalStatusBadge(selectedFile.approval_status).class} cursor-help transition-opacity hover:opacity-80`}
                      title={getApprovalStatusTooltip(selectedFile.approval_status)}
                    >
                      {getApprovalStatusBadge(selectedFile.approval_status).label}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowTriageModal(false);
                    selectFile(null);
                    setPreviewData(null);
                    setPreviewError(null);
                  }}
                  className="text-dark-text-muted hover:text-white ml-4"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="mt-4 flex gap-2 border-b border-dark-border">
                <button
                  onClick={() => setActiveTab('preview')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'preview'
                      ? 'border-accent-blue text-accent-blue'
                      : 'border-transparent text-dark-text-muted hover:text-dark-text'
                  }`}
                >
                  Preview
                </button>
                <button
                  onClick={() => setActiveTab('metadata')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'metadata'
                      ? 'border-accent-blue text-accent-blue'
                      : 'border-transparent text-dark-text-muted hover:text-dark-text'
                  }`}
                >
                  Metadata
                </button>
                <button
                  onClick={() => setActiveTab('statistics')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'statistics'
                      ? 'border-accent-blue text-accent-blue'
                      : 'border-transparent text-dark-text-muted hover:text-dark-text'
                  }`}
                >
                  Statistics
                </button>
              </div>
            </div>

            {/* Modal Content - Tabs */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
              {/* Preview Tab */}
              {activeTab === 'preview' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-dark-text mb-3">Document Preview</h3>
                    <div className="bg-dark-bg border border-dark-border rounded-lg p-4 min-h-[300px] max-h-[400px] overflow-y-auto">
                      {isLoadingPreview ? (
                        <div className="flex items-center justify-center h-[300px]">
                          <div className="text-center">
                            <div className="w-8 h-8 border-4 border-accent-blue border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-dark-text-muted text-sm">Loading preview...</p>
                          </div>
                        </div>
                      ) : previewError ? (
                        <div className="flex items-center justify-center h-[300px]">
                          <div className="text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-red-400 mx-auto mb-3">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                            <p className="text-red-400 text-sm font-semibold mb-2">Failed to Load Preview</p>
                            <p className="text-dark-text-muted text-xs">{previewError}</p>
                            <button
                              onClick={() => fetchDocumentPreview(selectedFile.id)}
                              className="mt-3 bg-dark-card hover:bg-bridge-hover border border-dark-border text-white px-4 py-2 rounded-lg text-xs transition-colors"
                            >
                              Retry
                            </button>
                          </div>
                        </div>
                      ) : previewData ? (
                        <div>
                          {/* Metadata Header */}
                          {(previewData.metadata.title || previewData.metadata.author) && (
                            <div className="mb-4 pb-4 border-b border-dark-border">
                              {previewData.metadata.title && (
                                <div className="text-lg font-semibold text-white mb-2">{previewData.metadata.title}</div>
                              )}
                              {previewData.metadata.author && (
                                <div className="text-sm text-dark-text-muted">By {previewData.metadata.author}</div>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-xs text-dark-text-muted">
                                {previewData.metadata.page_count && (
                                  <span>{previewData.metadata.page_count} pages</span>
                                )}
                                {previewData.metadata.word_count && (
                                  <span>{previewData.metadata.word_count.toLocaleString()} words</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Extracted Text */}
                          <div className="text-dark-text text-sm leading-relaxed whitespace-pre-wrap">
                            {previewData.extracted_text}
                          </div>

                          {/* Truncation Notice */}
                          {previewData.full_text_length > previewData.extracted_text.length && (
                            <div className="mt-4 pt-4 border-t border-dark-border">
                              <p className="text-xs text-dark-text-muted italic">
                                Text truncated for preview. Showing {previewData.extracted_text.length.toLocaleString()} of {previewData.full_text_length.toLocaleString()} characters.
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-[300px]">
                          <div className="text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-12 h-12 text-dark-text-muted mx-auto mb-3 opacity-50">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            <p className="text-dark-text-muted text-sm">No preview available</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Processing Status Section */}
                  {(() => {
                    const processingUpdate = processingUpdates.get(selectedFile.id);
                    const isProcessing = selectedFile.processing_status === 'processing' || processingUpdate?.status === 'processing';
                    const isCompleted = selectedFile.processing_status === 'completed';
                    const isFailed = selectedFile.processing_status === 'failed';

                    // Helper function to get stage explanation
                    const getStageExplanation = (progress: number) => {
                      if (progress <= 5) return "Starting the document processing workflow";
                      if (progress <= 15) return "Retrieving your document from secure storage";
                      if (progress <= 30) return "Reading and extracting text from the document";
                      if (progress <= 45) return "Breaking document into searchable sections";
                      if (progress <= 60) return "Saving sections to your knowledge base";
                      if (progress <= 85) return "Creating AI search capabilities for instant retrieval";
                      if (progress < 100) return "Finalizing and preparing for use";
                      return "Processing complete";
                    };

                    // Active Processing State - Show even without WebSocket update
                    if (isProcessing) {
                      // Use WebSocket data if available, otherwise show database status
                      const progress = processingUpdate?.progress || 0;
                      const message = processingUpdate?.message || 'Processing in progress...';
                      const metadata = processingUpdate?.metadata || {};

                      return (
                        <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg p-6 border border-blue-500/30">
                          {/* Header with Icon */}
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">Processing Your Document</h3>
                              <p className="text-sm text-blue-200">{getStageExplanation(progress)}</p>
                            </div>
                          </div>

                          {/* Progress Bar with Gradient */}
                          <div className="mb-4">
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-white font-medium">{message}</span>
                              {progress > 0 && <span className="text-blue-200 font-semibold">{progress}%</span>}
                            </div>
                            <div className="w-full bg-dark-card rounded-full h-3 overflow-hidden">
                              {progress > 0 ? (
                                <div
                                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500 ease-out"
                                  style={{ width: `${progress}%` }}
                                />
                              ) : (
                                <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full animate-pulse w-1/4" />
                              )}
                            </div>
                          </div>

                          {/* Processing Details Grid */}
                          {Object.keys(metadata).length > 0 && (
                            <div className="grid grid-cols-3 gap-3 mb-4">
                              {metadata.word_count && (
                                <div className="bg-dark-card/50 rounded-lg p-3 text-center">
                                  <div className="text-xl font-bold text-white">{metadata.word_count.toLocaleString()}</div>
                                  <div className="text-xs text-dark-text-muted mt-1">Words</div>
                                </div>
                              )}
                              {metadata.page_count && (
                                <div className="bg-dark-card/50 rounded-lg p-3 text-center">
                                  <div className="text-xl font-bold text-white">{metadata.page_count}</div>
                                  <div className="text-xs text-dark-text-muted mt-1">Pages</div>
                                </div>
                              )}
                              {metadata.chunk_count && (
                                <div className="bg-dark-card/50 rounded-lg p-3 text-center">
                                  <div className="text-xl font-bold text-white">{metadata.chunk_count}</div>
                                  <div className="text-xs text-dark-text-muted mt-1">Sections</div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Status Indicator with Auto-Refresh */}
                          <div className="bg-dark-card/50 rounded-lg p-4 border border-blue-500/20 mb-3">
                            {processingUpdate ? (
                              <div className="flex items-start gap-3">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mt-1 flex-shrink-0"></div>
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-green-300 mb-1">🔴 Live Updates Active</p>
                                  <p className="text-xs text-green-200/70">Receiving real-time progress from server</p>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <svg className="w-5 h-5 text-yellow-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-yellow-300 mb-1">⚡ Processing Started - Connecting...</p>
                                  <p className="text-xs text-yellow-200/70 mb-2">
                                    The system is processing your document. Real-time updates will appear when available.
                                  </p>
                                  <button
                                    onClick={() => {
                                      refreshFile(selectedFile.id);
                                    }}
                                    className="text-xs bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 px-3 py-1.5 rounded border border-yellow-500/30 transition-colors flex items-center gap-2"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Refresh Status Now
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Background Processing Notice */}
                          <div className="bg-dark-card/50 rounded-lg p-3 border border-blue-500/20">
                            <div className="flex items-start gap-2">
                              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="text-sm text-blue-100">
                                <p className="font-medium mb-1">Processing continues in the background</p>
                                <p className="text-xs text-blue-200/80">You can safely close this window. The document will appear in your knowledge base when ready. Expected time: 30-60 seconds.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // Completed State
                    if (isCompleted) {
                      return (
                        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-lg p-6 border border-green-500/30">
                          {/* Success Header */}
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">Processing Complete!</h3>
                              <p className="text-sm text-green-200">Your document is ready for review</p>
                            </div>
                          </div>

                          {/* Processing Summary */}
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-white mb-3">What We Did:</h4>
                            <div className="space-y-2">
                              {selectedFile.extracted_word_count && (
                                <div className="flex items-center gap-2 text-sm text-green-100">
                                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>Extracted {selectedFile.extracted_word_count.toLocaleString()} words from your document</span>
                                </div>
                              )}
                              {selectedFile.chunks_created && (
                                <div className="flex items-center gap-2 text-sm text-green-100">
                                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>Created {selectedFile.chunks_created} searchable sections</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-sm text-green-100">
                                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Generated AI embeddings for instant semantic search</span>
                              </div>
                            </div>
                          </div>

                          {/* Next Steps */}
                          <div className="bg-dark-card/50 rounded-lg p-4 border border-green-500/20">
                            <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                              Next Steps
                            </h4>
                            <div className="text-sm text-green-100 space-y-1">
                              <p><span className="font-medium">1.</span> Review the extracted content in the Preview tab</p>
                              <p><span className="font-medium">2.</span> Verify the document information is correct</p>
                              <p><span className="font-medium">3.</span> Click "Approve & Add to KB" to make it searchable</p>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // Failed State
                    if (isFailed) {
                      return (
                        <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-lg p-6 border border-red-500/30">
                          {/* Error Header */}
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                              <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">Processing Failed</h3>
                              <p className="text-sm text-red-200">We encountered an issue processing this document</p>
                            </div>
                          </div>

                          {/* Error Details */}
                          {selectedFile.processing_error && (
                            <div className="bg-dark-card/50 rounded-lg p-3 mb-4 border border-red-500/20">
                              <p className="text-sm font-medium text-red-200 mb-1">Error Details:</p>
                              <p className="text-sm text-red-100/80 font-mono">{selectedFile.processing_error}</p>
                            </div>
                          )}

                          {/* Common Causes */}
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-white mb-2">Common Causes:</h4>
                            <ul className="text-sm text-red-100 space-y-1 list-disc list-inside">
                              <li>The document might be corrupted or password-protected</li>
                              <li>The file format might not be fully supported</li>
                              <li>The document might be too large or complex</li>
                            </ul>
                          </div>

                          {/* Recovery Options */}
                          <div className="bg-dark-card/50 rounded-lg p-4 border border-red-500/20">
                            <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              What You Can Do
                            </h4>
                            <div className="text-sm text-red-100 space-y-2">
                              <p><span className="font-medium">1.</span> Try re-uploading the document (click "Retry" below)</p>
                              <p><span className="font-medium">2.</span> Check if the file opens correctly on your computer</p>
                              <p><span className="font-medium">3.</span> Try converting to a different format (PDF works best)</p>
                            </div>
                            <button
                              onClick={() => {
                                // Trigger retry by calling the processing endpoint again
                                toast.success('Retrying document processing...');
                                // You would implement retry logic here
                              }}
                              className="mt-3 w-full bg-red-500/20 hover:bg-red-500/30 text-red-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-red-500/30"
                            >
                              Retry Processing
                            </button>
                          </div>
                        </div>
                      );
                    }

                    // Pending State
                    if (selectedFile.processing_status === 'pending') {
                      return (
                        <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 rounded-lg p-6 border border-yellow-500/30">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                              <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">Ready to Process</h3>
                              <p className="text-sm text-yellow-200">Processing will start automatically</p>
                            </div>
                          </div>

                          <div className="bg-dark-card/50 rounded-lg p-4 border border-yellow-500/20 mb-4">
                            <h4 className="text-sm font-medium text-white mb-3">What Will Happen:</h4>
                            <div className="space-y-2">
                              <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-xs font-bold text-yellow-300">1</span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white">Text Extraction</p>
                                  <p className="text-xs text-yellow-100/80">We'll read all text from your document</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-xs font-bold text-yellow-300">2</span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white">Smart Chunking</p>
                                  <p className="text-xs text-yellow-100/80">Break into logical, searchable sections</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-xs font-bold text-yellow-300">3</span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white">AI Embedding Generation</p>
                                  <p className="text-xs text-yellow-100/80">Create semantic search capabilities</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-sm text-yellow-200 bg-dark-card/50 rounded-lg p-3 border border-yellow-500/20">
                            <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p>Processing typically takes 30-60 seconds depending on document size</p>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })()}
                </div>
              )}

              {/* Metadata Tab */}
              {activeTab === 'metadata' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-dark-text mb-3">File Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">File Size</div>
                        <div className="text-white">{formatFileSize(selectedFile.file_size_bytes)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">MIME Type</div>
                        <div className="text-white text-sm">{selectedFile.mime_type}</div>
                      </div>
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">Uploaded</div>
                        <div className="text-white text-sm">{formatDate(selectedFile.created_at)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">File ID</div>
                        <div className="text-white text-xs font-mono">{selectedFile.id.substring(0, 8)}...</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-dark-text">Denominational Context</h3>
                      {!isEditingMetadata ? (
                        <button
                          onClick={handleEditMetadata}
                          className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 text-xs rounded-lg transition-colors flex items-center gap-1.5"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancelEdit}
                            disabled={isSavingMetadata}
                            className="px-3 py-1.5 bg-dark-card hover:bg-bridge-hover border border-dark-border text-dark-text text-xs rounded-lg transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveMetadata}
                            disabled={isSavingMetadata}
                            className="px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-300 text-xs rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
                          >
                            {isSavingMetadata ? (
                              <>
                                <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Saving...
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Save
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    {!isEditingMetadata ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-dark-text-muted mb-1">Denomination</div>
                            <div className="text-white capitalize">{selectedFile.denomination_id.replace(/_/g, ' ')}</div>
                          </div>
                          <div>
                            <div className="text-xs text-dark-text-muted mb-1">Document Type</div>
                            <div className="text-white capitalize">{selectedFile.document_type.replace(/_/g, ' ')}</div>
                          </div>
                          <div>
                            <div className="text-xs text-dark-text-muted mb-1">Official Document</div>
                            <div className="text-white">{selectedFile.is_official ? 'Yes' : 'No'}</div>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-dark-text-muted mb-1">Trigger Words</div>
                          <div className="text-white text-sm">{selectedFile.trigger_words || <span className="text-dark-text-muted italic">No trigger words set</span>}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-dark-text-muted mb-1 block">Denomination</label>
                            <select
                              value={metadataEditForm.denomination_id}
                              onChange={(e) => setMetadataEditForm({ ...metadataEditForm, denomination_id: e.target.value })}
                              className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text focus:outline-none focus:border-accent-blue"
                            >
                              <option value="assemblies_of_god">Assemblies of God</option>
                              <option value="catholic">Catholic</option>
                              <option value="cogic">COGIC</option>
                              <option value="lutheran_elca">Lutheran (ELCA)</option>
                              <option value="lutheran_lcms">Lutheran (LCMS)</option>
                              <option value="national_baptist">National Baptist</option>
                              <option value="nondenominational">Nondenominational</option>
                              <option value="presbyterian">Presbyterian</option>
                              <option value="southern_baptist">Southern Baptist</option>
                              <option value="united_methodist">United Methodist</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-dark-text-muted mb-1 block">Document Type</label>
                            <select
                              value={metadataEditForm.document_type}
                              onChange={(e) => setMetadataEditForm({ ...metadataEditForm, document_type: e.target.value })}
                              className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text focus:outline-none focus:border-accent-blue"
                            >
                              <option value="statement_of_faith">Statement of Faith</option>
                              <option value="official_manual">Official Manual</option>
                              <option value="articles_of_religion">Articles of Religion</option>
                              <option value="confession_of_faith">Confession of Faith</option>
                              <option value="catechism">Catechism</option>
                              <option value="constitution">Constitution</option>
                              <option value="bylaws">Bylaws</option>
                              <option value="discipline">Discipline</option>
                              <option value="book_of_order">Book of Order</option>
                              <option value="book_of_concord">Book of Concord</option>
                              <option value="creed">Creed</option>
                              <option value="doctrine">Doctrine</option>
                              <option value="teaching">Teaching</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-dark-text-muted mb-1 block">
                            Trigger Words
                            <span className="ml-1 text-dark-text-muted italic">(comma-separated)</span>
                          </label>
                          <textarea
                            value={metadataEditForm.trigger_words}
                            onChange={(e) => setMetadataEditForm({ ...metadataEditForm, trigger_words: e.target.value })}
                            placeholder="e.g., baptism, communion, governance, Westminster"
                            className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text focus:outline-none focus:border-accent-blue resize-none"
                            rows={3}
                          />
                          <p className="text-xs text-dark-text-muted mt-1.5 flex items-start gap-1.5">
                            <svg className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Add keywords that should trigger the AI to reference this document for context</span>
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-dark-text-muted mb-1 block">Document Title</label>
                          <input
                            type="text"
                            value={metadataEditForm.extracted_title}
                            onChange={(e) => setMetadataEditForm({ ...metadataEditForm, extracted_title: e.target.value })}
                            className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-2 text-sm text-dark-text focus:outline-none focus:border-accent-blue"
                            placeholder="Enter document title"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-dark-text-muted mb-1 block">
                            Folder Path
                            <span className="ml-1 text-dark-text-muted italic">(for organization)</span>
                          </label>
                          <FolderSelector
                            value={metadataEditForm.folder_path}
                            onChange={(path) => setMetadataEditForm({ ...metadataEditForm, folder_path: path })}
                            disabled={isSavingMetadata}
                            className="w-full"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {(selectedFile.extracted_title || selectedFile.extracted_author || selectedFile.extracted_word_count) && (
                    <div>
                      <h3 className="text-sm font-semibold text-dark-text mb-3">Extracted Metadata</h3>
                      <div className="space-y-3">
                        {selectedFile.extracted_title && (
                          <div>
                            <div className="text-xs text-dark-text-muted mb-1">Title</div>
                            <div className="text-white">{selectedFile.extracted_title}</div>
                          </div>
                        )}
                        {selectedFile.extracted_author && (
                          <div>
                            <div className="text-xs text-dark-text-muted mb-1">Author</div>
                            <div className="text-white">{selectedFile.extracted_author}</div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          {selectedFile.extracted_page_count && (
                            <div>
                              <div className="text-xs text-dark-text-muted mb-1">Pages</div>
                              <div className="text-white">{selectedFile.extracted_page_count}</div>
                            </div>
                          )}
                          {selectedFile.extracted_word_count && (
                            <div>
                              <div className="text-xs text-dark-text-muted mb-1">Words</div>
                              <div className="text-white">{selectedFile.extracted_word_count.toLocaleString()}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedFile.processing_error && (
                    <div>
                      <h3 className="text-sm font-semibold text-red-400 mb-3">Processing Error</h3>
                      <div className="text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        {selectedFile.processing_error}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Statistics Tab */}
              {activeTab === 'statistics' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-dark-text mb-3">Processing Statistics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">Processing Status</div>
                        <div className="text-white capitalize">{selectedFile.processing_status}</div>
                      </div>
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">Approval Status</div>
                        <div className="text-white capitalize">{selectedFile.approval_status.replace(/_/g, ' ')}</div>
                      </div>
                      {selectedFile.chunks_created !== null && selectedFile.chunks_created > 0 && (
                        <>
                          <div>
                            <div className="text-xs text-dark-text-muted mb-1">Chunks Created</div>
                            <div className="text-white">{selectedFile.chunks_created}</div>
                          </div>
                          {selectedFile.chunking_strategy && (
                            <div>
                              <div className="text-xs text-dark-text-muted mb-1">Chunking Strategy</div>
                              <div className="text-white capitalize">{selectedFile.chunking_strategy}</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-dark-text mb-3">Document Statistics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">File Size</div>
                        <div className="text-white">{formatFileSize(selectedFile.file_size_bytes)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">File Type</div>
                        <div className="text-white uppercase">{selectedFile.file_type}</div>
                      </div>
                      {selectedFile.extracted_page_count && (
                        <div>
                          <div className="text-xs text-dark-text-muted mb-1">Total Pages</div>
                          <div className="text-white">{selectedFile.extracted_page_count}</div>
                        </div>
                      )}
                      {selectedFile.extracted_word_count && (
                        <div>
                          <div className="text-xs text-dark-text-muted mb-1">Total Words</div>
                          <div className="text-white">{selectedFile.extracted_word_count.toLocaleString()}</div>
                        </div>
                      )}
                      {selectedFile.extracted_word_count && selectedFile.chunks_created && selectedFile.chunks_created > 0 && (
                        <div>
                          <div className="text-xs text-dark-text-muted mb-1">Avg Words per Chunk</div>
                          <div className="text-white">{Math.round(selectedFile.extracted_word_count / selectedFile.chunks_created)}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-dark-text mb-3">Storage Information</h3>
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">Storage Key</div>
                        <div className="text-white text-xs font-mono break-all">{selectedFile.storage_key}</div>
                      </div>
                      <div>
                        <div className="text-xs text-dark-text-muted mb-1">Created</div>
                        <div className="text-white text-sm">{formatDate(selectedFile.created_at)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Action Buttons */}
            <div className="p-3 sm:p-4 lg:p-6 border-t border-dark-border bg-dark-bg/30">
              {selectedFile.processing_status === 'completed' && selectedFile.approval_status === 'pending_review' ? (
                <div className="space-y-4">
                  {/* Explanation Banner */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm">
                        <p className="font-medium text-white mb-1">Ready for Decision</p>
                        <p className="text-blue-200 text-xs">
                          <span className="font-medium">Approve</span> to add this document to your searchable Knowledge Base, or <span className="font-medium">Reject</span> to exclude it from search results.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Reject Button */}
                    <button
                      onClick={() => handleApprovalStatusUpdate('rejected')}
                      disabled={isUpdatingApproval}
                      className="bg-red-600/10 hover:bg-red-600/20 border border-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-red-200 px-4 py-4 rounded-lg transition-colors flex flex-col items-center gap-2 group"
                    >
                      <svg className="w-6 h-6 text-red-400 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <div className="text-center">
                        <div className="font-semibold text-sm">{isUpdatingApproval ? 'Updating...' : 'Reject Document'}</div>
                        <div className="text-xs text-red-300/80 mt-1">Exclude from search</div>
                      </div>
                    </button>

                    {/* Approve Button */}
                    <button
                      onClick={() => handleApprovalStatusUpdate('approved')}
                      disabled={isUpdatingApproval}
                      className="bg-green-600/10 hover:bg-green-600/20 border border-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-green-200 px-4 py-4 rounded-lg transition-colors flex flex-col items-center gap-2 group"
                    >
                      <svg className="w-6 h-6 text-green-400 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <div className="text-center">
                        <div className="font-semibold text-sm">{isUpdatingApproval ? 'Updating...' : 'Approve & Add to KB'}</div>
                        <div className="text-xs text-green-300/80 mt-1">Make searchable instantly</div>
                      </div>
                    </button>
                  </div>

                  {/* Download and Delete Options */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => downloadFile(selectedFile.id)}
                      className="bg-dark-card/50 hover:bg-dark-card border border-dark-border text-white px-4 py-2 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>

                    {!showDeleteConfirm ? (
                      <button
                        onClick={handleDeleteClick}
                        className="bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 px-4 py-2 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={handleDeleteCancel}
                          disabled={isDeleting}
                          className="flex-1 bg-dark-card hover:bg-bridge-hover border border-dark-border text-white px-3 py-2 rounded-lg transition-colors text-xs disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteConfirm}
                          disabled={isDeleting}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {isDeleting ? (
                            <>
                              <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              Confirm
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Helper Text */}
                  <div className="text-xs text-center text-dark-text-muted bg-dark-card/30 rounded-lg p-3">
                    <span className="font-medium">💡 Tip:</span> Documents added to the Knowledge Base (KB) become instantly searchable through semantic search and will be used to answer questions.
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => downloadFile(selectedFile.id)}
                    className="flex-1 bg-accent-blue hover:bg-accent-blue/80 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => refreshFile(selectedFile.id)}
                    className="flex-1 bg-dark-card hover:bg-bridge-hover border border-dark-border text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Refresh
                  </button>
                  {!showDeleteConfirm ? (
                    <button
                      onClick={handleDeleteClick}
                      className="flex-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 px-4 py-2 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  ) : (
                    <div className="flex-1 flex gap-2">
                      <button
                        onClick={handleDeleteCancel}
                        disabled={isDeleting}
                        className="flex-1 bg-dark-card hover:bg-bridge-hover border border-dark-border text-white px-3 py-2 rounded-lg transition-colors text-xs disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteConfirm}
                        disabled={isDeleting}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
                      >
                        {isDeleting ? (
                          <>
                            <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Confirm Delete
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Confirmation Modal (Step 2 & 3: Configure → Confirm) */}
      {showUploadConfirmModal && pendingFile && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
          <div className="bg-dark-card rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-dark-border">
              <h2 className="text-xl font-semibold text-white">Configure Upload</h2>
              <button
                onClick={handleCancelUpload}
                className="text-dark-text-muted hover:text-white transition-colors"
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              {/* File Info */}
              <div className="bg-dark-bg/50 border border-dark-border rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-accent-blue">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-dark-text-muted mb-1">Selected File</div>
                    <div className="text-white font-medium truncate">{pendingFile.name}</div>
                    <div className="text-xs text-dark-text-muted mt-1">
                      {(pendingFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Banner */}
              <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-accent-blue flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-dark-text-muted">
                    Configure how this document should be organized and processed in your knowledge base.
                  </p>
                </div>
              </div>

              {/* Configuration Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Denomination */}
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-2">
                      Denomination
                    </label>
                    <select
                      value={uploadConfigFormData.denomination_id}
                      onChange={(e) => setUploadConfigFormData({ ...uploadConfigFormData, denomination_id: e.target.value })}
                      className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue transition-all appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239091a4' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em',
                        paddingRight: '2.5rem',
                      }}
                    >
                      <option value="assemblies_of_god">Assemblies of God</option>
                      <option value="church_of_god">Church of God</option>
                      <option value="cogic">COGIC</option>
                      <option value="foursquare">Foursquare</option>
                      <option value="presbyterian">Presbyterian</option>
                      <option value="southern_baptist">Southern Baptist</option>
                      <option value="methodist">Methodist</option>
                      <option value="lutheran">Lutheran</option>
                      <option value="anglican">Anglican</option>
                    </select>
                  </div>

                  {/* Document Type */}
                  <div>
                    <label className="block text-sm font-medium text-dark-text mb-2">
                      Document Type
                    </label>
                    <select
                      value={uploadConfigFormData.document_type}
                      onChange={(e) => setUploadConfigFormData({ ...uploadConfigFormData, document_type: e.target.value })}
                      className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue transition-all appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239091a4' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em',
                        paddingRight: '2.5rem',
                      }}
                    >
                      <option value="confession_of_faith">Confession of Faith</option>
                      <option value="catechism">Catechism</option>
                      <option value="statement_of_faith">Statement of Faith</option>
                      <option value="constitution">Constitution</option>
                      <option value="bylaws">Bylaws</option>
                      <option value="policy">Policy</option>
                      <option value="manual">Manual</option>
                      <option value="minutes">Minutes</option>
                      <option value="sermon">Sermon</option>
                      <option value="liturgy">Liturgy</option>
                      <option value="history">History</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Folder Selection */}
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">
                    Destination Folder <span className="text-red-400">*</span>
                  </label>
                  <FolderSelector
                    value={uploadConfigFormData.folder_path}
                    onChange={(path) => setUploadConfigFormData({ ...uploadConfigFormData, folder_path: path })}
                    className="w-full"
                  />
                  <p className="text-xs text-dark-text-muted mt-1">
                    Select where this document should be stored in your denominational knowledge base
                  </p>
                </div>

                {/* Official Document Checkbox - Only visible to superusers */}
                {user?.is_superuser && (
                  <div>
                    <label className="flex items-start gap-3 cursor-pointer p-3 bg-dark-bg/30 border border-dark-border rounded-lg hover:bg-dark-bg/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={uploadConfigFormData.is_official}
                        onChange={(e) => setUploadConfigFormData({ ...uploadConfigFormData, is_official: e.target.checked })}
                        className="w-4 h-4 mt-0.5 text-accent-blue bg-dark-bg border-dark-border rounded focus:ring-2 focus:ring-accent-blue"
                      />
                      <div className="flex-1">
                        <span className="text-sm text-dark-text font-medium">Official Document</span>
                        <p className="text-xs text-dark-text-muted mt-1">
                          Mark this as an official denominational document with higher authority
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 sm:p-6 border-t border-dark-border bg-dark-bg/30">
              <button
                onClick={handleCancelUpload}
                className="flex-1 px-4 py-3 text-white bg-dark-card border border-dark-border rounded-lg hover:bg-dark-bg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={isUploading || !uploadConfigFormData.folder_path}
                className="flex-1 px-4 py-3 text-white bg-gradient-to-r from-accent-blue to-accent-orange rounded-lg hover:shadow-lg hover:shadow-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {isUploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Confirm Upload</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadView;
